import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Tag, Alert, Skeleton, Tooltip } from "antd"
import { useHistory, useParams } from "react-router-dom"

import {
    applyPreflightFilter,
    fetchPreflight,
    selectPreflight,
    selectPreflightLoading,
} from "../clientBatchingSlice"

// Worst first. The order matters more than the colours: a reviewer should never have to scroll to
// find the thing that stops a file being produced at all.
export const SEVERITY = {
    will_fail: { color: "red", label: "Will fail", blurb: "The file will not be produced at all." },
    will_be_wrong: { color: "orange", label: "Will be wrong", blurb: "A value will be incorrect, not merely missing." },
    will_be_blank: { color: "gold", label: "Will be blank", blurb: "A column will be empty." },
    not_checked: { color: "default", label: "Not checked", blurb: "This client's format is not in the pre-flight table, so nothing was verified." },
}

export default function PreflightFindings() {
    const dispatch = useDispatch()
    const history = useHistory()
    const { runId } = useParams()
    const preflight = useSelector(selectPreflight)
    const loading = useSelector(selectPreflightLoading)

    if (loading && !preflight) return <Skeleton active />
    if (!preflight) return null

    const findings = preflight.findings || []

    const viewBills = async (finding) => {
        await dispatch(applyPreflightFilter(finding))
        history.push(`/client_batching/runs/${runId}`)
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* one wrapper: .card-body is a flex row */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Pre-Flight</h2>
                            <div className="badge">{findings.length}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                {/* Every bill behind any finding, in one Review Bills view. */}
                                {findings.some((finding) => finding.bill_count > 0) && (
                                    <a
                                        href="javascript:;"
                                        onClick={() =>
                                            viewBills({ reason: "all", key: "all", message: "All pre-flight findings" })
                                        }
                                    >
                                        View All
                                    </a>
                                )}
                                {/* For fixes made from here without visiting Review Bills. */}
                                <a href="javascript:;" onClick={() => dispatch(fetchPreflight(runId, { force: true }))}>
                                    {loading ? "Re-checking…" : "Re-check"}
                                </a>
                            </div>
                        </div>
                    </div>

                    {findings.length === 0 && (
                        <Alert
                            type="success"
                            showIcon
                            message="Nothing to fix. Every client in this run has the ledger coding and vendor numbers its file needs."
                        />
                    )}

                    {findings.length > 0 && (
                        <p className="p-10">
                            These are warnings, not blockers — you can still finalize. Fixing them first means the
                            client's file arrives complete.
                        </p>
                    )}

                    {findings.length > 0 && (
                        <Table dataSource={findings} rowKey={(r) => `${r.client_id}-${r.reason}-${r.key}`} pagination={false}>
                            <Table.Column
                                title="Severity"
                                key="severity"
                                width={140}
                                render={(text, record) => {
                                    const meta = SEVERITY[record.severity] || {}
                                    return (
                                        <Tooltip title={meta.blurb}>
                                            <Tag color={meta.color}>{meta.label}</Tag>
                                        </Tooltip>
                                    )
                                }}
                            />
                            <Table.Column title="Client" key="client_name" dataIndex="client_name" />
                            <Table.Column title="Subject" key="subject" dataIndex="subject" />
                            <Table.Column
                                title="Problem"
                                key="message"
                                render={(text, record) => (
                                    <div>
                                        <div>{record.message}</div>
                                        <div style={{ opacity: 0.7, marginTop: "4px" }}>{record.remedy}</div>
                                    </div>
                                )}
                            />
                            <Table.Column
                                title="Bills"
                                key="bill_count"
                                width={120}
                                render={(text, record) =>
                                    record.bill_count > 0 ? (
                                        <a href="javascript:;" onClick={() => viewBills(record)}>
                                            View {record.bill_count}
                                        </a>
                                    ) : (
                                        "—"
                                    )
                                }
                            />
                        </Table>
                    )}
                </div>
            </div>
        </div>
    )
}
